import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import RawMarkup from '../../../common/RawMarkup';

const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    if (path.startsWith('/uploads')) {
        const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:1337";
        return `${baseUrl}${path}`;
    }
    return path;
};

const NosotrosDynamicSection = ({ data }) => {
    const [flippedIndex, setFlippedIndex] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    if (!data) return null;

    const renderTextSection = () => (
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#eff2f9', textAlign: 'center' }}>
            <Container maxWidth="lg">
                {data.title && (
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 800, 
                        color: '#4e429c', 
                        fontSize: { xs: '1.8rem', md: '2.8rem' } 
                    }}>
                        <RawMarkup content={data.title} />
                    </Typography>
                )}
                <Typography variant="body1" sx={{ 
                    maxWidth: '1000px', 
                    mx: 'auto', 
                    lineHeight: 1.6, 
                    color: '#555', 
                    fontSize: { xs: '1rem', md: '1.2rem' } 
                }}>
                    <RawMarkup content={data.content || data.description} />
                </Typography>
            </Container>
        </Box>
    );

    const renderInfoSection = () => {
        const isSolutions = data.title && data.title.toLowerCase().includes('soluciones');
        const isTargetAudience = data.title && data.title.toLowerCase().includes('para quién');
        const isUseCases = data.title && data.title.toLowerCase().includes('casos de uso');
        const isWhatItSolves = data.title && data.title.toLowerCase().includes('qué resuelve');
        const isDistribution = data.title && data.title.toLowerCase().includes('habilitamos');
        const isTeesSteps = data.title && data.title.toLowerCase().includes('etapas');

        const displayItems = (isUseCases && (!data.items || data.items.length === 0)) ? [
            {
                title: "Caso 1 — B2B",
                description: "Mayorista / distribuidor<!-- MORE --><div style='color: #1a237e; font-size: 0.95rem; line-height: 1.8;'><p><strong>Cliente tipo:</strong> Mayorista con red B2B en [mercado]</p><p><strong>Desafío:</strong> Cotización lenta + múltiples fuentes + operación manual</p><p><strong>Implementación:</strong> Engine + Integraciones + Automatización</p><p><strong>Resultados:</strong> Reducción de tiempo de cotización [50%], mejora de productividad [20%], impacto en conversión [10%]</p></div>",
                icon: "1"
            },
            {
                title: "Caso 2 — B2B2C",
                description: "Marca blanca / embebido<!-- MORE --><div style='color: #1a237e; font-size: 0.95rem; line-height: 1.8;'><p><strong>Cliente tipo:</strong> Partner que vende al pasajero final con tecnología integrable</p><p><strong>Desafío:</strong> Experiencia inconsistente + bajo ratio checkout + falta de medición</p><p><strong>Implementación:</strong> Engine + Content & Distribution + Diagnostics</p><p><strong>Resultados:</strong> Mejora en conversión/abandono/performance [20%]</p></div>",
                icon: "2"
            },
            {
                title: "Caso 3 — B2C",
                description: "Canal directo<!-- MORE --><div style='color: #1a237e; font-size: 0.95rem; line-height: 1.8;'><p><strong>Cliente tipo:</strong> Marca con web/app y objetivo de crecimiento directo</p><p><strong>Desafío:</strong> Abandono en checkout + fricción en búsqueda + sobrecarga operativa</p><p><strong>Implementación:</strong> Engine + Diagnostics + Automatización + Integraciones</p><p><strong>Resultados:</strong> Mejora de conversión [20%], reducción de fricción [50%], eficiencia operativa [50%]</p></div>",
                icon: "3"
            }
        ] : data.items;

        const renderTeesStepsLayout = () => (
            <Grid container spacing={6} alignItems="center" sx={{ mt: 4 }}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {data.items && data.items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Typography variant="h2" sx={{ 
                                    color: '#4DB081', 
                                    fontWeight: 900, 
                                    mr: 4, 
                                    lineHeight: 1,
                                    fontSize: { xs: '3rem', md: '4.5rem' },
                                    opacity: 0.8,
                                    minWidth: '60px'
                                }}>
                                    {idx + 1}
                                </Typography>
                                <Box>
                                    <Typography variant="h6" sx={{ color: '#4DB081', fontWeight: 800, mb: 1, fontSize: '1.25rem' }}>
                                        <RawMarkup content={item.title} />
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6 }}>
                                        <RawMarkup content={item.description} />
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box 
                        component="img" 
                        src="/assets/nosotros/tees-steps.png" 
                        alt="Implementación TEES" 
                        sx={{ 
                            width: '100%', 
                            height: 'auto', 
                            maxHeight: '600px',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.15))'
                        }} 
                    />
                </Grid>
            </Grid>
        );

        const renderUseCasesLayout = () => (
            <Grid container spacing={0} sx={{ mt: 4, bgcolor: 'white', borderRadius: 6, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.04)', border: '1px solid #eee' }}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ p: 3, bgcolor: '#fbfbfb', height: '100%', borderRight: '1px solid #eee' }}>
                        {displayItems.map((item, idx) => {
                            const [label, subtext] = (item.title || "").split('—');
                            const isActive = activeTab === idx;
                            return (
                                <Box
                                    key={idx}
                                    onClick={() => setActiveTab(idx)}
                                    sx={{
                                        p: 3, mb: 1, borderRadius: 4, cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        bgcolor: isActive ? '#d1f7e6' : 'transparent',
                                        transform: isActive ? 'scale(1.02)' : 'scale(1)',
                                        '&:hover': { bgcolor: isActive ? '#d1f7e6' : 'rgba(0,0,0,0.02)' }
                                    }}
                                >
                                    <Typography variant="h5" sx={{
                                        color: isActive ? '#4DB081' : '#424242',
                                        fontWeight: 800, fontSize: '1.4rem', mb: 0.5
                                    }}>
                                        {label} {subtext && <span style={{ fontWeight: 400, opacity: 0.8 }}>— {subtext}</span>}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: isActive ? '#4DB081' : '#757575', fontWeight: isActive ? 600 : 400 }}>
                                        <RawMarkup content={item.description.split('<!-- MORE -->')[0]} />
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{ p: { xs: 4, md: 6 }, display: 'flex', alignItems: 'flex-start', height: '100%' }}>
                        <Box sx={{ 
                            width: '100%',
                            '& p': { mb: 3, fontSize: '1.1rem', color: '#444' },
                            '& strong': { color: '#4e429c', fontWeight: 800, mr: 1, display: 'inline-block', minWidth: '140px' }
                        }}>
                            {displayItems[activeTab] && (
                                <Box sx={{ animation: 'fadeIn 0.5s ease' }}>
                                    <RawMarkup content={displayItems[activeTab].description.split('<!-- MORE -->')[1]} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );

        return (
            <Box sx={{ 
                pt: isWhatItSolves ? 0 : { xs: 8, md: 10 }, 
                pb: isDistribution ? 0 : { xs: 8, md: 10 }, 
                bgcolor: '#eff2f9' 
            }}>
                <Container maxWidth="lg">
                    {!isWhatItSolves && (data.title || data.subtitle) && (
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            {data.title && (
                                <Typography variant="h3" sx={{ 
                                    mb: 3, 
                                    fontWeight: 800, 
                                    color: '#4e429c', 
                                    fontSize: { xs: '1.8rem', md: '2.8rem' } 
                                }}>
                                    <RawMarkup content={data.title} />
                                </Typography>
                            )}
                            {data.subtitle && (
                                <Typography variant="h6" sx={{ 
                                    maxWidth: '1000px', 
                                    mx: 'auto', 
                                    lineHeight: 1.5, 
                                    color: '#555', 
                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                    fontWeight: 400
                                }}>
                                    <RawMarkup content={data.subtitle} />
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isTeesSteps ? renderTeesStepsLayout() : isUseCases ? renderUseCasesLayout() : (
                        <Grid container spacing={4} justifyContent="center">
                            {data.items && data.items.map((item, idx) => {
                                const isVerticals = data.title && data.title.toLowerCase().includes('verticales');
                                const isTEES = data.title && data.title.toUpperCase().includes('TEES');
                                const isDistribution = data.title && data.title.toLowerCase().includes('habilitamos');
                                const isWhatItSolves = data.title && data.title.toLowerCase().includes('qué resuelve');
                                const isServicios = data.title && data.title.toLowerCase().includes('servicios');
                                const isAlianzas = data.title && data.title.toLowerCase().includes('alianzas');
                                const mdSize = isAlianzas ? 2 : (data.items.length === 4 ? 3 : (data.items.length === 2 ? 6 : (isDistribution ? 12 : 4)));

                                if (isAlianzas) {
                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={idx}>
                                            <Paper elevation={1} sx={{ 
                                                p: 3, 
                                                height: 140, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                borderRadius: 4,
                                                bgcolor: 'white',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { 
                                                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                                                    transform: 'translateY(-5px)'
                                                }
                                            }}>
                                                {item.icon && (item.icon.startsWith('/') || item.icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) ? (
                                                    <Box 
                                                        component="img" 
                                                        src={getImageUrl(item.icon)} 
                                                        alt={item.title} 
                                                        sx={{ 
                                                            maxWidth: '100%', 
                                                            maxHeight: '100%', 
                                                            objectFit: 'contain',
                                                            transition: 'all 0.3s ease'
                                                        }} 
                                                    />
                                                ) : (
                                                    <RawMarkup content={item.icon} />
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                }
                                const [summary, ...details] = (item.description || '').split('<!-- MORE -->');

                                if (isVerticals) {
                                    return (
                                        <Grid item xs={12} sm={6} md={2.4} key={idx}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Box sx={{
                                                    width: 80, height: 80, border: '2px solid #3f51b5', borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    mx: 'auto', mb: 2, bgcolor: 'white', overflow: 'hidden'
                                                }}>
                                                    {item.icon && (item.icon.startsWith('/') || item.icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) ? (
                                                        <Box component="img" src={getImageUrl(item.icon)} alt={item.title} sx={{ width: 80, height: 80, objectFit: 'contain' }} />
                                                    ) : (
                                                        <Typography variant="h3"><RawMarkup content={item.icon || '🚀'} /></Typography>
                                                    )}
                                                </Box>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}><RawMarkup content={item.title} /></Typography>
                                            </Box>
                                        </Grid>
                                    );
                                }

                                if (isTEES) {
                                    let borderColor = '#3f51b5';
                                    if (item.title.includes('Essential')) borderColor = '#4DB081';
                                    else if (item.title.includes('Custom')) borderColor = '#ffab40';

                                    return (
                                        <Grid item xs={12} md={4} key={idx}>
                                            <Paper elevation={3} sx={{ height: '100%', borderRadius: 4, overflow: 'hidden', borderTop: `8px solid ${borderColor}`, p: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                                        {item.icon && (item.icon.startsWith('/') || item.icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) ? (
                                                            <Box component="img" src={getImageUrl(item.icon)} alt={item.title} sx={{ width: 40, height: 40, objectFit: 'contain' }} />
                                                        ) : (
                                                            <Box sx={{ fontSize: '2rem' }}><RawMarkup content={item.icon} /></Box>
                                                        )}
                                                    </Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: borderColor }}>{item.title}</Typography>
                                                </Box>
                                                <Typography variant="body1" color="text.secondary"><RawMarkup content={item.description} /></Typography>
                                            </Paper>
                                        </Grid>
                                    );
                                }

                                if (isDistribution) {
                                    return (
                                        <Grid item xs={12} key={idx}>
                                            <Box sx={{ 
                                                p: { xs: 3, md: 4 }, 
                                                mb: 1,
                                                borderLeft: '4px solid #4DB081',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { bgcolor: 'rgba(77, 176, 129, 0.05)' }
                                            }}>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#4DB081', mb: 2 }}>
                                                    <RawMarkup content={item.title} />
                                                </Typography>
                                                <Box sx={{ color: '#555', lineHeight: 1.8, fontSize: '1.05rem', maxWidth: '1100px' }}>
                                                    <RawMarkup content={item.description} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                }

                                if (isWhatItSolves) {
                                    return (
                                        <Grid item xs={12} key={idx}>
                                            <Box sx={{ 
                                                p: { xs: 4, md: 5 }, 
                                                bgcolor: 'transparent', 
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#4e429c', mb: 4 }}>
                                                    {item.title || data.title}
                                                </Typography>
                                                <Box sx={{ 
                                                    maxWidth: '950px', 
                                                    mx: 'auto', 
                                                    color: '#444', 
                                                    fontSize: '1.15rem',
                                                    '& ul': { 
                                                        listStyle: 'none', 
                                                        p: 0, m: 0,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 2
                                                    },
                                                    '& li': {
                                                        display: 'block',
                                                        position: 'relative',
                                                        pl: 4,
                                                        lineHeight: 1.6,
                                                        textAlign: 'left',
                                                        width: 'fit-content',
                                                        '&:before': {
                                                            content: '"✓"',
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: 0,
                                                            color: '#4DB081',
                                                            fontWeight: 900,
                                                            fontSize: '1.2rem'
                                                        }
                                                    },
                                                    '& br': { display: 'none' }
                                                }}>
                                                    <RawMarkup content={item.description} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                }

                                if (isSolutions) {
                                    const isFlipped = flippedIndex === idx;
                                    return (
                                        <Grid item xs={12} sm={6} md={mdSize} key={idx}>
                                            <Box sx={{ perspective: '1000px', height: 380, cursor: 'pointer' }}
                                                onClick={() => setFlippedIndex(isFlipped ? null : idx)}
                                                onMouseEnter={() => !('ontouchstart' in window) && setFlippedIndex(idx)}
                                                onMouseLeave={() => !('ontouchstart' in window) && setFlippedIndex(null)}
                                            >
                                                <Box sx={{ position: 'relative', width: '100%', height: '100%', transition: 'transform 0.6s', transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'none' }}>
                                                    <Paper elevation={2} sx={{ position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 4, bgcolor: 'white', textAlign: 'center' }}>
                                                        {item.icon && (
                                                            <Box sx={{ width: 70, height: 70, bgcolor: '#f0f9f5', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                                                                {item.icon && (item.icon.startsWith('/') || item.icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) ? (
                                                                    <Box component="img" src={getImageUrl(item.icon)} alt={item.title} sx={{ width: 40, height: 40, objectFit: 'contain' }} />
                                                                ) : (
                                                                    <Typography variant="h4"><RawMarkup content={item.icon} /></Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1a237e', fontSize: '1.2rem', textAlign: 'center' }}><RawMarkup content={item.title} /></Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6, fontSize: '0.95rem', px: 1, textAlign: 'center' }}>
                                                            <RawMarkup content={summary} />
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ mt: 'auto', pt: 2, color: '#4DB081', fontWeight: 'bold', textAlign: 'center' }}>SABER MÁS ↺</Typography>
                                                    </Paper>
                                                    <Paper elevation={4} sx={{ position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden', p: 3, display: 'flex', flexDirection: 'column', borderRadius: 4, bgcolor: 'white', transform: 'rotateY(180deg)', textAlign: 'left' }}>
                                                        <Typography variant="subtitle1" sx={{ color: '#4DB081', fontWeight: 'bold', mb: 1, borderBottom: '1px solid #4DB081', pb: 0.5, fontSize: '0.9rem' }}>
                                                            <RawMarkup content={item.title} />
                                                        </Typography>
                                                        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#4DB081', borderRadius: '4px' } }}>
                                                            {details.length > 0 && (() => {
                                                                const parts = details.join('').split(/<(h4|strong)[^>]*>Outputs:<\/(h4|strong)>/i).filter(p => !['h4', 'strong'].includes(p.toLowerCase()));
                                                                const isSingleColumn = parts.length === 1;
                                                                
                                                                return (
                                                                    <Grid container spacing={2} wrap={isSingleColumn ? "wrap" : "nowrap"}>
                                                                        {parts.map((part, pIdx) => (
                                                                            <Grid item xs={isSingleColumn ? 12 : 6} key={pIdx} sx={{ minWidth: isSingleColumn ? '100%' : '50%' }}>
                                                                                {!isSingleColumn && (
                                                                                    <Typography variant="caption" sx={{ color: '#4DB081', fontWeight: 700, display: 'block', mb: 1, textTransform: 'capitalize', letterSpacing: 0.5, pb: 0.5, textAlign: 'center' }}>
                                                                                        {pIdx === 0 ? 'Inputs' : 'Outputs'}
                                                                                    </Typography>
                                                                                )}
                                                                                <Box sx={{ 
                                                                                    fontSize: '0.7rem', 
                                                                                    color: '#666', 
                                                                                    '& h4, & strong': {
                                                                                        color: '#4DB081 !important',
                                                                                        fontWeight: '700 !important',
                                                                                        textTransform: 'capitalize',
                                                                                        display: 'block',
                                                                                        mb: 1,
                                                                                        fontSize: '0.75rem !important',
                                                                                        textAlign: 'center'
                                                                                    },
                                                                                    '& p, & li, & span, & ul': { 
                                                                                        m: 0, 
                                                                                        mb: 0.5, 
                                                                                        color: '#666 !important',
                                                                                        fontSize: '0.7rem',
                                                                                        fontWeight: 'normal !important',
                                                                                        textAlign: 'left'
                                                                                    },
                                                                                    '& ul': { pl: 1.5 },
                                                                                    '& hr': { display: 'none' }
                                                                                }}>
                                                                                    <RawMarkup content={pIdx === 0 ? part.replace(/<(h4|strong)[^>]*>Inputs:<\/(h4|strong)>/i, '') : part} />
                                                                                </Box>
                                                                            </Grid>
                                                                        ))}
                                                                    </Grid>
                                                                );
                                                            })()}
                                                        </Box>
                                                    <Typography variant="caption" sx={{ mt: 'auto', pt: 1, borderTop: '1px solid #4DB081', textAlign: 'center', color: '#4DB081', fontWeight: 'bold' }}>VOLVER ↺</Typography>
                                                </Paper>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    );
                                }

                                return (
                                    <Grid item xs={12} sm={6} md={mdSize} key={idx}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 4,
                                                height: '100%',
                                                borderRadius: 6,
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
                                                transition: 'transform 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-10px)',
                                                },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                bgcolor: 'white'
                                            }}
                                        >
                                            {item.icon && !isServicios && !isTargetAudience && (
                                                <Box sx={{
                                                    width: 70,
                                                    height: 70,
                                                    bgcolor: '#e0e7ff',
                                                    borderRadius: 3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mb: 3,
                                                    flexShrink: 0,
                                                    border: '1px solid #c7d2fe'
                                                }}>
                                                    {item.icon && (item.icon.startsWith('/') || item.icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) ? (
                                                        <Box 
                                                            component="img" 
                                                            src={getImageUrl(item.icon)} 
                                                            alt={item.title}
                                                            sx={{ width: 45, height: 45, objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="h4" component="div">
                                                            <RawMarkup content={item.icon} />
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    fontWeight: 800, 
                                                    color: '#4e429c',
                                                    fontSize: '1.25rem',
                                                    mb: 2
                                                }}>
                                                    <RawMarkup content={item.title} />
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                    lineHeight: 1.5,
                                                    color: '#666',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    <RawMarkup content={item.description} />
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    {data.footerText && (
                        <Box sx={{ mt: 6, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#666', maxWidth: '800px', lineHeight: 1.6 }}>
                                <RawMarkup content={data.footerText} />
                            </Typography>
                        </Box>
                    )}
                </Container>
            </Box>
        );
    };

    switch (data.__component) {
        case 'nosotros.text-section':
            return renderTextSection();
        case 'nosotros.info-section':
            return renderInfoSection();
        default:
            return null;
    }
};

export default NosotrosDynamicSection;
